import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ProjectionAssumptions } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { count, percent } from '../../lib/format';
import { pricedPlan } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { BarChart, Frame, HeadRow } from './primitives';
import { kpiLabelForGoal, kpiLabelSingular } from './kpi';

/** "CTR 2.0% · Conversion rate 4.0%", or the generic fallback when the deck
 * carries no plan-level override (design.md V15: "state the plan-level
 * rates when mediaPlan.assumptions carries them; otherwise 'Based on
 * channel benchmarks'"). */
function assumptionsTagline(assumptions: ProjectionAssumptions | null | undefined): string {
  const parts: string[] = [];
  if (assumptions?.ctr != null) parts.push(`CTR ${percent(assumptions.ctr, 1)}`);
  if (assumptions?.conversionRate != null) parts.push(`Conversion rate ${percent(assumptions.conversionRate, 1)}`);
  return parts.length > 0 ? parts.join(' · ') : 'Based on channel benchmarks';
}

/**
 * One projections tile whose derived value can be replaced by a rep-typed
 * override in `content.fields` (brief §4 `14 projections`: "Authors can
 * override any value; overrides are flagged in the editor"). The override
 * is a free-form display string — no arithmetic happens on it, it just
 * replaces what would otherwise render — and the "Overridden" mark is
 * workspace-only (design.md V15 overrides rule: "flagged... never in
 * print/share").
 */
function ProjectionTile({
  label,
  derived,
  overrideValue,
  readOnly,
  onSave,
  note,
}: {
  label: string;
  derived: string | null;
  overrideValue: string | undefined;
  readOnly: boolean;
  onSave: (next: string) => void;
  note?: string;
}): ReactElement | null {
  const resolve = useBindings();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(overrideValue ?? '');
  const ref = useRef<HTMLInputElement>(null);
  const hasOverride = Boolean(overrideValue?.trim());
  // `overrideValue` is `content.fields.*` - free text an agent (or rep) can
  // set, so it can carry a live {{binding}}; `derived` is a computed number
  // string and never needs resolving.
  const display = hasOverride ? resolve((overrideValue as string).trim()) : derived;

  useEffect(() => {
    if (!editing) setDraft(overrideValue ?? '');
  }, [overrideValue, editing]);
  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  if (readOnly && !display) return null;

  const valueNode = readOnly ? (
    <span className="mv3-stat-tile-value">{display ?? '—'}</span>
  ) : editing ? (
    <input
      ref={ref}
      className="mv3-stat-tile-value mv3-stat-tile-input"
      value={draft}
      placeholder={derived ?? ''}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        const next = draft.trim();
        if (next !== (overrideValue ?? '').trim()) onSave(next);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          ref.current?.blur();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(overrideValue ?? '');
          setEditing(false);
        }
      }}
    />
  ) : (
    <span
      className="mv3-stat-tile-value mv3-stat-tile-value-editable"
      role="button"
      tabIndex={0}
      title="Click to override"
      onClick={() => setEditing(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') setEditing(true);
      }}
    >
      {display ?? '—'}
    </span>
  );

  return (
    <div className="mv3-stat-tile mv3-projection-tile">
      <span className="mv3-stat-tile-label">{label}</span>
      {valueNode}
      {hasOverride && !readOnly ? <span className="mv3-override-mark">Overridden</span> : null}
      {note ? <span className="mv3-stat-tile-note">{note}</span> : null}
    </div>
  );
}

/**
 * projections (brief §4 `14`): FULL light, KPI tiles + chart. Reads the
 * active tier's `PricedPlan.estConversionsMonthly` (V15: Σ non-null
 * `PricedLine.estConversionsMonthly`, itself clicks/impressions/units ×
 * benchmark or `mediaPlan.assumptions` rates). When there is nothing to
 * project — no benchmarks ever produced a number — the workspace shows a
 * quiet prompt and print/share renders nothing (design.md V15 acceptance:
 * never a number typed on a slide, and never a blank promise either).
 */
export function ProjectionsPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const plan = pricedPlan(proposal);
  const target = plan?.estConversionsMonthly ?? null;
  const kpiLabel = kpiLabelForGoal(proposal.objective.primaryGoal);
  const tagline = assumptionsTagline(proposal.mediaPlan.assumptions);
  const fields = section.content.fields ?? {};
  const setField = (key: string, value: string) => {
    actions.saveContent(section, { fields: { ...fields, [key]: value } });
  };

  if (target === null) {
    return (
      <PageShell
        section={section}
        readOnly={readOnly}
        onTitleSave={(title) => actions.saveTitle(section, title)}
        className="mv3-projections-root"
        hideTitle
      >
        <Frame surface="light" proposal={proposal} section={section}>
          <div className="mv3-projections-body">
            <HeadRow title="What success looks like" tagline={tagline} />
            {!readOnly ? <p className="mv3-projections-empty-note">Add benchmarks to project results.</p> : null}
          </div>
        </Frame>
      </PageShell>
    );
  }

  const durationMonths = Math.max(proposal.budget.durationMonths, 1);
  const conservative = target * 0.75;
  const costPer = target > 0 ? (plan?.totalMonthly ?? 0) / target : null;
  const termTotal = target * durationMonths;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-projections-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-projections-body">
          <HeadRow title="What success looks like" tagline={tagline} />
          <div className="mv3-projections-layout">
            <div className="mv3-projections-tiles">
              <ProjectionTile
                label={`${kpiLabel} / month`}
                derived={`${count(conservative)} → ${count(target)}`}
                overrideValue={fields.kpiMonthly}
                readOnly={readOnly}
                onSave={(next) => setField('kpiMonthly', next)}
                note="Conservative → target"
              />
              <ProjectionTile
                label={`Cost per ${kpiLabelSingular(kpiLabel)}`}
                derived={costPer !== null ? `$${costPer.toFixed(2)}` : null}
                overrideValue={fields.costPer}
                readOnly={readOnly}
                onSave={(next) => setField('costPer', next)}
              />
              <ProjectionTile
                label={`${durationMonths}-month total`}
                derived={count(termTotal)}
                overrideValue={fields.termTotal}
                readOnly={readOnly}
                onSave={(next) => setField('termTotal', next)}
              />
            </div>
            <div className="mv3-projections-chart-col">
              <BarChart
                seriesA={{ label: 'Conservative', values: Array.from({ length: durationMonths }, () => conservative) }}
                seriesB={{ label: 'Target', values: Array.from({ length: durationMonths }, () => target) }}
                height={220}
              />
              <p className="mv3-projections-chart-caption">
                {kpiLabel} by month · conservative vs. target
              </p>
            </div>
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
